import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { DateTime } from 'luxon';
import { compareTwoStrings } from 'string-similarity';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { ExternalContactPerson } from '../master-data-management/users/entities/ExternalContactPerson.js';
import { ExternerMitarbeiter } from '../master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '../master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '../master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '../master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { ContextPrefix } from '../permissions.js';
import { Role } from '../semantics/Role.js';
import { DuplicateDataTracker } from './DuplicateDataTracker.js';

function extractUsernameFromEmail(email: string) {
  if (!email) return null;
  const atIndex = email.indexOf('@');
  if (atIndex !== -1) {
    return email.substring(0, atIndex);
  }
  return email;
}

// TODO Felix: abstract this into database, so it can be chaged in runtime
const weights = {
  _id: 0,
  _class: 0,
  email: 0.1,
  firstname: 0.2,
  lastname: 0.2,
  birthday: 0.1,
  kpfID: 0.1,
  zip: 0.1,
  city: 0.1,
  street: 0.05,
  housenumber: 0.05,
};

const useSimilarityScoreForValue = {
  _id: false,
  _class: false,
  email: true,
  firstname: true,
  lastname: true,
  birthday: false,
  kpfID: false,
  zip: false,
  city: false,
  street: true,
  housenumber: false,
};

const blacklistValues = ['Nicht angegeben'];

class DuplicateSearchProxy {
  public _id: Maybe<string>;
  public _class: Maybe<string>;
  public email: Maybe<string>;
  public firstname: Maybe<string>;
  public lastname: Maybe<string>;
  public birthday: Maybe<string>;
  public kpfID: Maybe<string>;
  public zip: Maybe<string>;
  public city: Maybe<string>;
  public street: Maybe<string>;
}

export function clamp(num, min, max) {
  return num <= min ? min : num >= max ? max : num;
}

function getSimilarity(a: DuplicateSearchProxy, b: DuplicateSearchProxy, key: string) {
  let similarity = 0.0;
  if (useSimilarityScoreForValue[key]) {
    similarity = compareTwoStrings(a[key], b[key]);
  } else {
    similarity = a[key] === b[key] ? 1.0 : 0.0;
  }

  return similarity * weights[key];
}

export function getSimilarityBetweenProxies(a: DuplicateSearchProxy, b: DuplicateSearchProxy): number {
  let similarity = 0.0;
  let numberOfComparisons = 0;
  // Go trough each key and check similarity
  for (const key of Object.getOwnPropertyNames(a)) {
    if (!a[key] || !b[key] || blacklistValues.includes(a[key]) || blacklistValues.includes(b[key])) continue;
    similarity += getSimilarity(a, b, key);
    numberOfComparisons++;
  }

  // Normalise similarity because some entities dont have the same number of keys to compare
  const multiplier = 2 - numberOfComparisons / Object.getOwnPropertyNames(a).length;
  const normalisedSimilarity = similarity * multiplier;
  return normalisedSimilarity;
}

export function getProxyForUser(user: EntityData<ChancenwerkUser>) {
  // TODO we only check the first kpf. Do we need to check multiple. Works 99% of the time
  const firstKPFIDForStudent = user.roles
    .find(r => r.name === Role.STUDENT)
    ?.contexts.at(0)
    ?.replace(ContextPrefix.KPF, '');

  // Construct duplicate proxy
  const duplicateProxy: DuplicateSearchProxy = {
    _id: user._id,
    _class: user._class,
    email: extractUsernameFromEmail(user.email),
    firstname: user.vorname,
    lastname: user.nachname,
    birthday: user.geburtstag ? DateTime.fromJSDate(user.geburtstag).toFormat('dd:LL:yyyy') : null,
    kpfID: firstKPFIDForStudent,
    zip: user.adresse?.zipCity?.zip,
    city: user.adresse?.zipCity?.city,
    street: user.adresse?.strasse,
  };

  return duplicateProxy;
}

export async function searchAllAccounts(similarityThreshold: number) {
  logger.info('Startet searching for duplicates');
  // Only search for active students and other persons. This way we dont need to load more than 20k users but only a few
  const selector = {
    $or: [
      {
        $and: [
          {
            _class: Student._class,
          },
          {
            $or: [
              { 'kuendigung.austrittsdatum': { $exists: false } },
              { 'kuendigung.austrittsdatum': null },
              { 'kuendigung.austrittsdatum': undefined },
              { 'kuendigung.austrittsdatum': { $gt: DateTime.now().toJSDate() } },
            ],
          },
        ],
      },
      {
        _class: {
          $in: [
            VerwaltungsMitarbeiter._class,
            Mitarbeiter._class,
            ExternerMitarbeiter._class,
            ExternalContactPerson._class,
          ],
        },
      },
    ],
  };

  // Map with the lastname as the key. If the lastname is not the same the entries are most likely not a duplicate
  const userDuplicateMap = new Map<string, DuplicateSearchProxy[]>();
  const possibleDuplicates: DuplicateDataTracker[] = [];

  const userCollection = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);
  const trackerCollection = await getRawCollection<EntityData<DuplicateDataTracker>>(
    DuplicateDataTracker._collectionName,
  );

  const userCursor = userCollection.find(selector);
  for await (const user of userCursor) {
    // Build the possible duplicate key for the user. If no key is found we skip the user (users without email or nachname cant be duplicate because the cannot be natural persons)
    const keyForUser = user.nachname ? user.nachname : user.email;
    if (!keyForUser) continue;

    const duplicateProxy = getProxyForUser(user);

    // Add to the array for the lastname
    const proxiesInMap = userDuplicateMap.get(keyForUser);
    if (!proxiesInMap) {
      userDuplicateMap.set(keyForUser, [duplicateProxy]);
      continue;
    }

    for (const proxyInMap of proxiesInMap) {
      const similarity = getSimilarityBetweenProxies(proxyInMap, duplicateProxy);
      if (similarity < similarityThreshold) continue;

      const possibleBlacklistedTracker = await trackerCollection.findOne({
        $or: [
          {
            entityID: duplicateProxy._id,
            duplicateID: proxyInMap._id,
            blacklisted: true,
          },
          {
            entityID: proxyInMap._id,
            duplicateID: duplicateProxy._id,
            blacklisted: true,
          },
        ],
      });

      if (possibleBlacklistedTracker) continue;

      possibleDuplicates.push(
        new DuplicateDataTracker({
          entityID: duplicateProxy._id,
          duplicateID: proxyInMap._id,
          probabilityOfDuplicate: clamp(similarity, 0.0, 1.0),
          entityCollection: ChancenwerkUser._collectionName,
          duplicateCollection: ChancenwerkUser._collectionName,
          blacklisted: false,
        }),
      );
    }
  }

  for (const v of possibleDuplicates) {
    await v.create();
  }
  logger.info('Ended searching for duplicates. Added new: ' + possibleDuplicates.length);
  return possibleDuplicates.length;
}
